/**
 * usePhotoCapture — orchestrates the client-side capture pipeline.
 *
 * FT-342 (2026-07-08) — capture pipeline orchestrator.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.2.
 *
 * Flow per file:
 *   1. MIME sniff → HEIC decode (lazy) if needed
 *   2. Strip GPS EXIF (JPEG only; pass-through otherwise). Non-GPS EXIF —
 *      including DateTimeOriginal (shutter-time) — is preserved.
 *   3. Extract DateTimeOriginal from EXIF (pre-strip) to use as capturedAt.
 *      Falls back to Date.now() if unavailable (e.g. gallery photo w/o EXIF).
 *   4. Decode with EXIF orientation + resize (long edge cap 1920 or 2048)
 *   5. Encode full-size JPEG
 *   6. Generate 256×256 thumbnail from the same bitmap
 *   7. Save via usePhotoStore.savePhoto → PhotoRef
 *
 * Multi-file processing is SEQUENTIAL. Fan-out is an OOM hazard on old iPhones
 * with <2 GB RAM. The processing loop can be aborted via the returned `cancel`.
 *
 * FT-342 pre-merge fixes:
 *   - Concurrency guard: only one pipeline in flight at a time. A second call
 *     rejects immediately with a clear error (prevents double-tap OOM on old
 *     iPhones).
 *   - Timeout-aware queue: when `withTimeout` rejects, the underlying pipeline
 *     promise still runs to completion. The in-flight counter tracks the RAW
 *     pipeline promise, not the timeout wrapper — so a retry cannot stack a
 *     new pipeline on top of a hanging one.
 *
 * Per-photo timeout: 15 s. On timeout the caller sees a clear error, but the
 * background pipeline continues until it settles (JS has no true cancel). New
 * capture attempts remain blocked until that background settle.
 */

import { useCallback, useRef, useState } from 'react';
import { isHeic, heicDecode } from '../pipeline/heicDecode';
import { stripGpsAndExif } from '../pipeline/stripGps';
import { decodeWithOrientation } from '../pipeline/exifOrientation';
import { resizeAndEncodeWithinBudget } from '../pipeline/resizeAndEncode';
import { generateThumbnail } from '../pipeline/generateThumbnail';
import { generatePhotoId } from '../pipeline/uuid';
import { usePhotoStore } from './usePhotoStore';
import { computePhotoPushBodyBytes, isPhotoTooLargeError } from '../../../../utils/photoPayloadBudget';

/**
 * FT-577: thrown by processFiles when a batch did not fully succeed. Carries the
 * refs of photos that WERE saved, so the caller can still attach them to the
 * record (previously a mid-batch error discarded already-saved photos' refs).
 */
export class PhotoBatchError extends Error {
  constructor(message, { refs = [], rejectedCount = 0, total = 0, cause } = {}) {
    super(message);
    this.name = 'PhotoBatchError';
    this.refs = refs;
    this.rejectedCount = rejectedCount;
    this.total = total;
    this.cause = cause;
  }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const PER_PHOTO_TIMEOUT_MS = 15000;
const QUALITY_LONG_EDGE = { standard: 1920, high: 2048 };
const ALREADY_IN_FLIGHT_MSG =
  'Photo capture already in progress — wait for the current photo to finish';

/**
 * @param {string} vesselId  required — scopes photo storage
 * @returns capture API — see JSDoc on each field
 */
export function usePhotoCapture(vesselId) {
  const store = usePhotoStore(vesselId);
  // `busy` mirrors inFlightRef so React re-renders when the guard flips.
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const cancelledRef = useRef(false);
  // Counts RAW pipeline promises that have not yet settled. Timeout wrappers
  // are NOT counted — the raw promise's .finally() is the sole decrement site.
  const inFlightRef = useRef(0);

  const acquireSlot = useCallback(() => {
    if (inFlightRef.current > 0) {
      throw new Error(ALREADY_IN_FLIGHT_MSG);
    }
    inFlightRef.current += 1;
    setBusy(true);
  }, []);

  const releaseSlot = useCallback(() => {
    inFlightRef.current = Math.max(0, inFlightRef.current - 1);
    if (inFlightRef.current === 0) setBusy(false);
  }, []);

  /**
   * Cancel any in-flight processing loop. In-progress stages will complete
   * for the current photo (createImageBitmap is not abortable), but no
   * further files will be processed and no more IDB writes will occur.
   */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  /**
   * Process a single file end-to-end. Returns the PhotoRef the parent should
   * push onto its record. Rejects immediately if another capture is already
   * running (double-tap guard).
   * @param {File|Blob} file
   * @param {Object} options { recordType, recordId, capturedBy, quality?, caption? }
   */
  const processFile = useCallback(async (file, options) => {
    if (!vesselId) throw new Error('[usePhotoCapture] vesselId is required');
    if (!file) throw new Error('[usePhotoCapture] file is required');
    if (!options?.recordType || !options?.recordId || !options?.capturedBy) {
      throw new Error('[usePhotoCapture] recordType, recordId, capturedBy are required');
    }
    // Concurrency guard — throws synchronously (well, at first await) if busy.
    acquireSlot();
    return runPipeline(file, { ...options, vesselId }, store, releaseSlot);
  }, [vesselId, store, acquireSlot, releaseSlot]);

  /**
   * Process multiple files sequentially. Any per-file error aborts the batch
   * and re-throws so the caller can surface a toast. Rejects immediately if
   * another capture (single or batch) is already running.
   * @param {(File|Blob)[]} files
   */
  const processFiles = useCallback(async (files, options) => {
    if (!Array.isArray(files) || files.length === 0) return [];
    // Batch shares the same in-flight counter, so overlapping single-shot
    // calls during a batch (or vice-versa) are blocked.
    acquireSlot();
    cancelledRef.current = false;
    setProgress({ current: 0, total: files.length, stage: 'starting' });
    const refs = [];
    let tooLarge = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        if (cancelledRef.current) break;
        setProgress({ current: i + 1, total: files.length, stage: 'processing' });
        try {
          // Per-file runs share the slot the batch already holds — pass a no-op
          // release so runPipeline doesn't double-decrement.
          // eslint-disable-next-line no-await-in-loop -- SEQUENTIAL by design (OOM guard)
          const ref = await runPipeline(files[i], { ...options, vesselId }, store, () => {});
          refs.push(ref);
        } catch (err) {
          // FT-577: a photo that can't be made small enough is SKIPPED (not
          // saved) and reported; the rest of the batch carries on.
          if (isPhotoTooLargeError(err)) { tooLarge += 1; continue; }
          // Any other error still aborts the batch (as before) — but the refs
          // of photos ALREADY saved are handed back instead of being dropped.
          throw new PhotoBatchError(err?.message || 'Photo capture failed.', {
            refs: refs.slice(), total: files.length, cause: err,
          });
        }
      }
    } finally {
      releaseSlot();
      setProgress(null);
    }
    if (tooLarge > 0) {
      const saved = refs.length;
      const message = saved === 0
        ? (tooLarge === 1
          ? 'This photo is too large to upload, even after shrinking it, so it has NOT been saved. Please try a different photo.'
          : `${plural(tooLarge, 'photo was', 'photos were')} too large to upload, even after shrinking, so none were saved. Please try different photos.`)
        : `${plural(tooLarge, 'photo was', 'photos were')} too large to upload, even after shrinking, and ${tooLarge === 1 ? 'was' : 'were'} NOT saved. The other ${plural(saved, 'photo was', 'photos were')} saved.`;
      throw new PhotoBatchError(message, { refs: refs.slice(), rejectedCount: tooLarge, total: files.length });
    }
    return refs;
  }, [vesselId, store, acquireSlot, releaseSlot]);

  return {
    processFile,
    processFiles,
    isProcessing: busy,
    progress,
    cancel,
  };
}

/**
 * The pure pipeline runner. Extracted so tests can exercise it without
 * mounting the hook. Bitmap is always closed on the way out to avoid leaks.
 *
 * `onRawSettle` fires when the RAW pipeline promise settles (resolve OR
 * reject) — used by the hook to decrement its in-flight counter only after
 * the underlying work actually completes, so a timeout does not free the slot
 * while background work is still holding memory.
 */
async function runPipeline(file, options, store, onRawSettle = () => {}) {
  const quality = options.quality === 'high' ? 'high' : 'standard';
  const photoId = generatePhotoId();

  const work = (async () => {
    // 1. HEIC decode (lazy)
    let workingBlob = file;
    if (await isHeic(file)) {
      workingBlob = await heicDecode(workingBlob);
    }
    // 2. Extract shutter-time from EXIF BEFORE the GPS strip (both dumps are
    //    non-destructive but reading first keeps intent clear).
    const capturedAt = (await extractCapturedAt(workingBlob)) ?? Date.now();
    // 3. Strip GPS (JPEG only; pass-through otherwise). Non-GPS EXIF (incl.
    //    DateTimeOriginal) is preserved.
    //
    // FT-349: GPS-per-recordType policy. GPS EXIF corroborates evidence for
    // equipment / maintenance / defect photos (subject = a THING at a KNOWN
    // LOCATION); stripped only for photos of PEOPLE (crew_profile) where GPS
    // is a privacy leak. Deliberate MCA-evidence policy — locked by advisor
    // 2026-07-09 (FT-349). See also pipeline/stripGps.js header note.
    const shouldStripGps = options?.recordType === 'crew_profile';
    if (shouldStripGps) {
      workingBlob = await stripGpsAndExif(workingBlob);
    }
    // 4. Decode + orient + resize in one pass
    const targetWidth = QUALITY_LONG_EDGE[quality];
    const bitmap = await decodeWithOrientation(workingBlob, targetWidth);
    try {
      // 5. Thumbnail FIRST (same bitmap — decode once). FT-577: its size is
      //    part of the upload body, so it must be known before the full image
      //    is fitted to the budget.
      const thumb = await generateThumbnail(bitmap);
      const meta = {
        photoId,
        recordType: options.recordType,
        recordId: options.recordId,
        mime: 'image/jpeg',
        capturedAt,
        capturedBy: options.capturedBy,
        ...(options.caption ? { caption: options.caption } : {}),
      };
      // 6. Full-size JPEG, FT-577 budget-enforced: measures the EXACT
      //    /sync/push body (full + thumb base64 + record fields). Throws
      //    PhotoTooLargeError (plain-English, shown by the caller) if it can't
      //    be made to fit — nothing is saved in that case.
      const encoded = await resizeAndEncodeWithinBudget(bitmap, quality, {
        ...(options.payloadBudgetBytes ? { budgetBytes: options.payloadBudgetBytes } : {}),
        measure: (blob, width, height) => computePhotoPushBodyBytes({
          photoData: { ...meta, width, height, bytes: blob.size },
          fullBytes: blob.size,
          fullMime: blob.type || 'image/jpeg',
          thumbBytes: thumb.blob.size,
          thumbMime: thumb.blob.type || 'image/jpeg',
          vesselId: options.vesselId,
        }),
      });
      // 7. Persist to IDB via FT-341 store (store re-checks the budget too)
      const ref = await store.savePhoto(
        {
          ...meta,
          blob: encoded.blob,
          width: encoded.width,
          height: encoded.height,
          bytes: encoded.bytes,
        },
        thumb.blob,
      );
      return ref;
    } finally {
      // Close the bitmap on ALL exit paths (success + error) — leak guard.
      try { bitmap.close && bitmap.close(); } catch { /* noop */ }
    }
  })();

  // Decrement the in-flight counter ONLY when the raw pipeline settles, not
  // when the timeout wrapper fires. This is the timeout-aware queue guarantee.
  // We attach a SILENT observer that swallows rejection so the raw promise
  // never surfaces as an unhandled rejection when the caller only listens
  // through the timeout wrapper's own reject path.
  work.then(
    () => { try { onRawSettle(); } catch { /* noop */ } },
    () => { try { onRawSettle(); } catch { /* noop */ } },
  );

  return withTimeout(work, PER_PHOTO_TIMEOUT_MS);
}

/**
 * Read DateTimeOriginal (EXIF shutter-time) from a JPEG blob. Returns a JS
 * timestamp (ms) or null if unavailable.
 *
 * DateTimeOriginal format is `"YYYY:MM:DD HH:MM:SS"` (EXIF spec — colons in
 * date part, no timezone). We parse conservatively: if the value isn't a
 * plausible date string, return null and let the caller fall back to Date.now().
 */
async function extractCapturedAt(blob) {
  if (!isJpegBlob(blob)) return null;
  try {
    const piexifMod = await import('piexifjs');
    const piexif = piexifMod.default || piexifMod;
    const dataUrl = await blobToDataUrl(blob);
    const exif = piexif.load(dataUrl);
    const raw = exif?.Exif?.[piexif.ExifIFD.DateTimeOriginal];
    if (!raw || typeof raw !== 'string') return null;
    return parseExifDateTime(raw);
  } catch {
    return null;
  }
}

function parseExifDateTime(s) {
  // "YYYY:MM:DD HH:MM:SS" — replace date colons with dashes so Date can parse.
  const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  // Local-time — EXIF has no TZ. Building via Date() constructor treats it as
  // local, which is the convention the shutter time was written in.
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se));
  const t = dt.getTime();
  return Number.isFinite(t) ? t : null;
}

function isJpegBlob(blob) {
  return blob && typeof blob.type === 'string' && /^image\/jpe?g$/i.test(blob.type);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Reject with a clear message if `promise` does not settle within `ms`.
 * The underlying work continues in the background (JS has no true cancel);
 * the timeout only prevents the caller from waiting forever. See runPipeline
 * — the in-flight counter is bound to the raw promise, not this wrapper.
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`[usePhotoCapture] photo processing timed out after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

// Exported for direct testing without mounting the hook.
export { runPipeline, ALREADY_IN_FLIGHT_MSG };
export default usePhotoCapture;
